import { useCallback, useEffect, useMemo, useRef } from 'react';
import { Platform } from 'react-native';
import AsyncStorage from '@react-native-async-storage/async-storage';
import type { Trip } from '@/services/trips';
import {
  scheduleSmartCueReminder,
  cancelSmartCueReminder,
  requestNotificationPermissions,
} from '@/services/notifications';
import type { TripAlert } from '@/utils/tripAlerts';
import { isCategoryEnabled, isTripMuted, isInQuietHours, sendGatedNotification, createNotificationRequest, DEFAULT_COOLDOWNS } from '@/services/notificationGate';
import { buildDedupeKey } from '@/services/notificationDedupe';
import type { NotificationCategory } from '@/state/userStore';
import { useNotificationStore } from '@/notifications/store';
import { getTripAlertReadTimestamps } from '@/utils/tripAlertReadPersistence';

type StoredNotification = {
  key: string; // cueId@days
  notificationId: string;
  tripId: string;
};

const STORAGE_KEY = '@tripflow_scheduled_smart_cues';
const REMINDER_DAYS = [14, 7, 3, 1];
const TRIGGER_HOUR_LOCAL = 9; // schedule in the morning local time (avoids "midnight already passed" skips)
const COMPLETED_TRIP_STATUSES = new Set(['completed', 'complete', 'finished', 'done', 'past']);

/**
 * Per-trip scheduling mutex.
 *
 * Why: effects can run concurrently (fast state churn, remounts, strict mode, etc).
 * If two runs schedule the same cue before either persists state, iOS will deliver duplicates.
 * Serializing by tripId ensures idempotency.
 */
const tripSyncQueue = new Map<string, Promise<void>>();
const SMART_CUE_SYNC_COOLDOWN_MS = 15_000;
const recentSmartCueSyncByTrip = new Map<string, { digest: string; at: number }>();
const enqueueTripSync = (tripId: string, task: () => Promise<void>) => {
  const prev = tripSyncQueue.get(tripId) ?? Promise.resolve();
  const next = prev
    .catch(() => {
      // Swallow to keep the chain alive
    })
    .then(task)
    .finally(() => {
      // Only clear if we're still the last enqueued task
      if (tripSyncQueue.get(tripId) === next) {
        tripSyncQueue.delete(tripId);
      }
    });
  tripSyncQueue.set(tripId, next);
  return next;
};

/**
 * Map alert types to notification categories for preference checking
 */
const alertTypeToCategory: Record<string, NotificationCategory> = {
  missing_transport: 'itinerary',
  missing_lodging: 'itinerary',
  visa: 'documents',
  check_in: 'itinerary',
  docs: 'documents',
  collaborators: 'collaboration',
};

const loadStored = async (): Promise<StoredNotification[]> => {
  try {
    const raw = await AsyncStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    if (Array.isArray(parsed)) return parsed as StoredNotification[];
    return [];
  } catch {
    return [];
  }
};

const persistStored = async (entries: StoredNotification[]) => {
  try {
    await AsyncStorage.setItem(STORAGE_KEY, JSON.stringify(entries));
  } catch {
    // Swallow errors; notifications are best-effort
  }
};

export const clearStoredSmartCues = async (): Promise<void> => {
  try {
    const stored = await loadStored();
    await Promise.all(stored.map((entry) => cancelSmartCueReminder(entry.notificationId)));
    await AsyncStorage.removeItem(STORAGE_KEY);
  } catch {
    // Best-effort cleanup for logout/account switches.
  }
};

const isReadinessSchedulingEligible = (trip: Trip): boolean => {
  const now = new Date();
  const start = trip.startDate instanceof Date ? trip.startDate : new Date(trip.startDate);
  const end = trip.endDate instanceof Date ? new Date(trip.endDate) : new Date(trip.endDate);

  if (!Number.isFinite(start.getTime()) || !Number.isFinite(end.getTime())) {
    return false;
  }
  end.setHours(23, 59, 59, 999);

  const isCompletedByDate = end < now;
  const isCompletedByStatus = Boolean(
    trip.status && COMPLETED_TRIP_STATUSES.has(trip.status.trim().toLowerCase()),
  );
  const hasStarted = now >= start;

  return !trip.isArchived && !hasStarted && !isCompletedByDate && !isCompletedByStatus;
};

export const useSmartCueNotifications = (alerts: TripAlert[], trip: Trip | null): void => {
  const storedRef = useRef<StoredNotification[] | null>(null);
  const loadingRef = useRef<boolean>(false);
  const hasSeenEligibleAlertPayloadRef = useRef<boolean>(false);
  const eligibleEmptyRunsRef = useRef<number>(0);

  const tripId = trip?.id;
  // Stable selector identity: inline arrows change every render and recreate Zustand's
  // useSyncExternalStore getSnapshot, which can cause "Maximum update depth exceeded".
  const selectTransportReadAt = useCallback(
    (s: ReturnType<typeof useNotificationStore.getState>) =>
      tripId ? s.items[`missing-transport-${tripId}`]?.readAt : undefined,
    [tripId],
  );
  const selectLodgingReadAt = useCallback(
    (s: ReturnType<typeof useNotificationStore.getState>) =>
      tripId ? s.items[`missing-lodging-${tripId}`]?.readAt : undefined,
    [tripId],
  );
  const transportReadAt = useNotificationStore(selectTransportReadAt);
  const lodgingReadAt = useNotificationStore(selectLodgingReadAt);

  const alertsKey = useMemo(() => {
    if (!Array.isArray(alerts) || alerts.length === 0) return '';
    return alerts.map((a) => a.id).sort().join('|');
  }, [alerts]);

  // Use numeric millis — Firestore often hands new Timestamp/Date object identities for the same instant;
  // depending on `trip` caused the scheduling effect to re-run every snapshot (Plan tab max update depth).
  const tripStartMs = trip?.startDate
    ? trip.startDate instanceof Date
      ? trip.startDate.getTime()
      : new Date(trip.startDate as string | number | Date).getTime()
    : NaN;
  const tripEndMs = trip?.endDate
    ? trip.endDate instanceof Date
      ? trip.endDate.getTime()
      : new Date(trip.endDate as string | number | Date).getTime()
    : NaN;

  const tripScheduleKey = useMemo(() => {
    if (!trip?.id) return '';
    if (!Number.isFinite(tripStartMs) || !Number.isFinite(tripEndMs)) return trip.id;
    return `${trip.id}:${tripStartMs}:${tripEndMs}:${trip.isArchived ? 1 : 0}`;
  }, [trip?.id, trip?.isArchived, tripStartMs, tripEndMs]);

  useEffect(() => {
    // New trip context: reset hydration heuristics and force reload from storage.
    hasSeenEligibleAlertPayloadRef.current = false;
    eligibleEmptyRunsRef.current = 0;
    storedRef.current = null;
    loadingRef.current = false;
  }, [trip?.id]);

  useEffect(() => {
    let cancelled = false;

    const syncNotifications = async () => {
      // Web does not support local notification scheduling; bail early to avoid noisy logs
      if (Platform.OS === 'web') return;
      if (!trip || !trip.startDate || !Array.isArray(alerts)) return;
      if (cancelled) return;

      const syncDigest = `${tripScheduleKey}|${alertsKey}|${transportReadAt ?? ''}|${lodgingReadAt ?? ''}`;
      const lastSync = recentSmartCueSyncByTrip.get(trip.id);
      if (lastSync && lastSync.digest === syncDigest && Date.now() - lastSync.at < SMART_CUE_SYNC_COOLDOWN_MS) {
        return;
      }

      const schedulingEligible = isReadinessSchedulingEligible(trip);
      // Guard against transient empty snapshots during subscription startup.
      // Without this, we cancel existing reminders then immediately re-add them.
      if (schedulingEligible) {
        if (alerts.length > 0) {
          hasSeenEligibleAlertPayloadRef.current = true;
          eligibleEmptyRunsRef.current = 0;
        } else {
          eligibleEmptyRunsRef.current += 1;
          // Startup: brief empty snapshots while bookings/itinerary load — defer one pass.
          // After we ever saw readiness cues then cleared them (user fixed trip), NEVER defer:
          // cancelling scheduled local reminders must run on that same alertsKey transition or deps
          // may not fire again until the stale notification fires.
          const shouldDeferEmptySync =
            !hasSeenEligibleAlertPayloadRef.current && eligibleEmptyRunsRef.current < 2;
          if (shouldDeferEmptySync) {
            return;
          }
        }
      } else {
        // Ineligible trips should cancel stale scheduled cues immediately.
        eligibleEmptyRunsRef.current = 0;
      }

      const hasPermission = await requestNotificationPermissions();
      if (!hasPermission) return;
      if (cancelled) return;
      // Load stored once
      if (!storedRef.current && !loadingRef.current) {
        loadingRef.current = true;
        storedRef.current = await loadStored();
        loadingRef.current = false;
      }
      if (cancelled) return;

      const readMap = await getTripAlertReadTimestamps();
      if (cancelled) return;

      const cueMarkedDone = (cueId: string): boolean => {
        const ra = useNotificationStore.getState().items[cueId]?.readAt;
        if (ra != null && ra > 0) return true;
        const t = readMap[cueId];
        return typeof t === 'number' && Number.isFinite(t) && t > 0;
      };

      const stored = storedRef.current || [];
      const desiredKeys = new Set<string>();
      const updated: StoredNotification[] = [...stored];

      const tripStart =
        trip.startDate instanceof Date ? trip.startDate : new Date(trip.startDate);
      // Normalize to a stable local time so day-based reminders don't land at midnight.
      const tripStartAtMorning = new Date(tripStart);
      tripStartAtMorning.setHours(TRIGGER_HOUR_LOCAL, 0, 0, 0);

      // Only schedule for missing-setup cues.
      // NOTE: event-driven alerts (e.g. flight delay/check-in, collaborator invite accepted, comments)
      // should come from server push, not local schedules.
      const cueCandidates = schedulingEligible
        ? alerts.filter(
            (a) =>
              (a.type === 'missing_transport' || a.type === 'missing_lodging') && !cueMarkedDone(a.id),
          )
        : [];
      let immediateQueuedThisRun = false;

      for (const cue of cueCandidates) {
        if (cancelled) return;
        // Check user preferences before scheduling
        const category = alertTypeToCategory[cue.type] || 'itinerary';
        if (!isCategoryEnabled(category)) {
          continue; // User has disabled this category
        }
        
        // Check if this trip is muted
        if (isTripMuted(trip.id)) {
          continue; // User has muted this trip
        }
        
        // Immediate nudge for urgent readiness alerts so users see more than weather notifications.
        // Only do this once per cue per trip.
        const immediateKey = `${cue.id}@immediate`;
        if (!desiredKeys.has(immediateKey)) {
          desiredKeys.add(immediateKey);
          const alreadyImmediate = updated.find((e) => e.key === immediateKey && e.tripId === trip.id);
          if (!alreadyImmediate && !immediateQueuedThisRun) {
            // Skip immediate notifications during quiet hours (unless high priority)
            if (cue.priority !== 'high' && isInQuietHours()) {
              // Don't schedule immediate during quiet hours
            } else {
              const triggerDate = new Date(Date.now() + 60 * 1000);
              const dedupeKey = buildDedupeKey.smartCue(trip.id, cue.id, 'immediate');
              await sendGatedNotification(
                createNotificationRequest({
                  category,
                  tripId: trip.id,
                  priority: cue.priority,
                  dedupeKey,
                  cooldownMs: DEFAULT_COOLDOWNS.smartCueImmediate,
                }),
                async () => {
                  const notificationId = await scheduleSmartCueReminder({
                    tripId: trip.id,
                    cueId: cue.id,
                    cueKey: immediateKey,
                    title: cue.title,
                    body: cue.message,
                    triggerDate,
                  }, { skipPermissionCheck: true });
                  if (!notificationId) {
                    // Don't mark as sent if iOS refused scheduling.
                    throw new Error('smart_cue_not_scheduled');
                  }
                  updated.push({ key: immediateKey, notificationId, tripId: trip.id });
                  immediateQueuedThisRun = true;
                },
              );
            }
          }
        }

        for (const days of REMINDER_DAYS) {
          if (cancelled) return;
          const triggerDate = new Date(tripStartAtMorning);
          triggerDate.setDate(triggerDate.getDate() - days);

          const key = `${cue.id}@${days}`;
          // Only schedule future triggers
          if (triggerDate.getTime() <= Date.now()) continue;

          desiredKeys.add(key);
          const already = updated.find((e) => e.key === key && e.tripId === trip.id);
          if (!already) {
            const notificationId = await scheduleSmartCueReminder({
              tripId: trip.id,
              cueId: cue.id,
              cueKey: key,
              title: cue.title,
              body: cue.message,
              triggerDate,
            }, { skipPermissionCheck: true });
            if (notificationId) {
              updated.push({ key, notificationId, tripId: trip.id });
            }
          }
        }
      }

      // Cancel any scheduled notifications that are no longer desired
      const toRemove: StoredNotification[] = [];
      for (const entry of updated) {
        if (cancelled) return;
        if (entry.tripId !== trip.id) continue;
        if (!desiredKeys.has(entry.key)) {
          await cancelSmartCueReminder(entry.notificationId);
          toRemove.push(entry);
        }
      }

      if (toRemove.length) {
        const remaining = updated.filter((e) => !toRemove.includes(e));
        storedRef.current = remaining;
        await persistStored(remaining);
      } else {
        storedRef.current = updated;
        await persistStored(updated);
      }
      recentSmartCueSyncByTrip.set(trip.id, { digest: syncDigest, at: Date.now() });
    };

    // Serialize scheduling per-trip to prevent duplicate notifications.
    if (trip?.id) {
      void enqueueTripSync(trip.id, syncNotifications);
    } else {
    syncNotifications().catch(() => {
      // Best-effort: ignore errors
    });
    }

    return () => {
      cancelled = true;
    };
  }, [alertsKey, tripScheduleKey, transportReadAt, lodgingReadAt]);
};
